import { treeStore } from '../state'
import type {
  EditorFormModel,
  EditorProductCreationModel,
  EditorRulingField,
  EditorSectionModel,
  EditorSectionName,
  EditorSettingValue,
  EditorSettingsTab,
} from '@/msgdeskRuntime/contracts/editorModel'
import { EDITOR_SETTING_NAMES } from '@/msgdeskRuntime/contracts/editorModel'
import { getMsgDeskRuntime } from '../runtime/MsgDeskRuntime'
import type { EditorNameKeyResult, EditorRecipientCommitResult, EditorRecipientSnapshot } from '../contracts/editorActions'
import type { LegacyRough } from '../model'
import { editorNameScope, localEditorName } from '../contracts/editorNameScope'

const numericSettingIndexes = new Set([1, 3, 5, 7, 9, 11, 13, 26, 27, 28, 29, 30, 31])
const checkboxSettingIndexes = new Set([
  0, 2, 4, 6, 8, 10, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
  35, // is_reg_users (REGISTERED USERS, docs task §5)
])

const moreSettingIndexByName = new Map<string, number>(EDITOR_SETTING_NAMES.map((name, index) => [name, index]))
const controlledComboIndexesByMaster = new Map<string, readonly number[]>([
  ['is_view', [1]],
  ['is_view_msges', [3]],
  ['is_view_answers', [5]],
  ['is_forbid', [7]],
  ['is_fold', [9]],
  ['is_freeze', [11]],
  ['is_hide', [13]],
  ['is_view_count', [26, 27, 28, 29, 30, 31]],
])

// Pair-combo ownership (legacy m23/m31): an unchecked master checkbox means
// the combo's ROUGH value is 0 even though the DOM displays option 1.
const comboMasterByName = new Map<string, string>([
  ['is_view_combo', 'is_view'],
  ['is_view_msges_combo', 'is_view_msges'],
  ['is_view_answers_combo', 'is_view_answers'],
  ['is_forbid_combo', 'is_forbid'],
  ['is_fold_combo', 'is_fold'],
  ['is_freeze_combo', 'is_freeze'],
  ['is_hide_combo', 'is_hide'],
  ['is_view_count_0', 'is_view_count'],
  ['is_view_count_1', 'is_view_count'],
  ['is_view_count_2', 'is_view_count'],
  ['is_view_count_3', 'is_view_count'],
  ['is_view_count_4', 'is_view_count'],
  ['is_view_count_5', 'is_view_count'],
])
const rulingIndexByField: Record<EditorRulingField, number> = {
  timeStart: 0,
  timeStop: 1,
  startEnabled: 3,
  stopEnabled: 4,
  hideTimeLocked: 5,
}
// Keep the existing seven legacy tab ids stable. Email is a new React
// subsection and receives the additive id 8 even though Configure presents it
// beside Rules.
const settingsTabs: EditorSettingsTab[] = ['display', 'rules', 'counts', 'timers', 'protect', 'avatar', 'page', 'email']

type LegacyRoughSection = 'body' | 'body_textarea' | 'to_address' | 'more_settings' | 'protect' | 'ruling' | 'attaches'

function readLegacyRough(): LegacyRough | undefined {
  try {
    return getMsgDeskRuntime().facade.editorRough()
  } catch {
    return undefined
  }
}

function cloneData<T>(value: T, depth = 0): T {
  if (value == null || typeof value !== 'object') return value
  if (depth > 8) return undefined as T
  if (typeof Element !== 'undefined' && value instanceof Element) return undefined as T
  if (Array.isArray(value)) return value.map(item => cloneData(item, depth + 1)) as T
  if ((value as any).jquery || typeof (value as any).arr_loop === 'function') return undefined as T

  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'function') continue
    out[key] = cloneData(item, depth + 1)
  }
  return out as T
}

function ensureRoughInputs(rough: LegacyRough): Record<string, unknown[]> {
  rough.inputs_values = rough.inputs_values || {}
  return rough.inputs_values
}

function ensureSection(rough: LegacyRough, section: LegacyRoughSection): unknown[] {
  const inputs = ensureRoughInputs(rough)
  if (!Array.isArray(inputs[section])) inputs[section] = []
  return inputs[section]
}

function legacySection(rough: LegacyRough, section: LegacyRoughSection): unknown[] {
  const value = rough.inputs_values?.[section]
  return Array.isArray(value) ? value : []
}

function stringValue(value: unknown): string | undefined {
  return value === undefined || value === null || typeof value === 'object' ? undefined : String(value)
}

function numberValue(value: unknown): number | undefined {
  const number = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(number) ? number : undefined
}

function legacyTrue(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

function readProductCreation(rough: LegacyRough): EditorProductCreationModel {
  const value = (rough as LegacyRough & { product_creation?: Record<string, unknown> }).product_creation
  if (!value || typeof value !== 'object') return {}
  return {
    preview: stringValue(value.preview),
    poaEnabled: typeof value.poa_enabled === 'boolean' ? value.poa_enabled : undefined,
  }
}

function writeProductCreation(rough: LegacyRough, model: EditorProductCreationModel): void {
  const target = rough as LegacyRough & { product_creation?: Record<string, unknown> }
  const current = target.product_creation && typeof target.product_creation === 'object'
    ? target.product_creation
    : {}
  if (model.preview !== undefined) current.preview = model.preview
  if (model.poaEnabled !== undefined) current.poa_enabled = model.poaEnabled
  target.product_creation = current
}

function readSectionStates(rough: LegacyRough): EditorFormModel['sections'] {
  const sections: EditorFormModel['sections'] = {}
  const elRough = rough.el_rough
  if (!elRough || typeof elRough !== 'object') return sections

  const settings = elRough.more_settings
  if (settings && typeof settings === 'object') {
    const tabId = numberValue(settings.tab_id)
    sections.settings = {
      open: typeof settings.is_open === 'boolean' ? settings.is_open : undefined,
      activeTab: tabId && settingsTabs[tabId - 1] ? settingsTabs[tabId - 1] : undefined,
    }
  }

  const formatting = elRough.msg_txt
  if (formatting && typeof formatting === 'object' && typeof formatting.is_rich_edit === 'boolean') {
    sections.formatting = { open: formatting.is_rich_edit }
  }

  const senderOptions = elRough.name_key
  if (senderOptions && typeof senderOptions === 'object' && typeof senderOptions.is_ext === 'boolean') {
    sections.senderOptions = { open: senderOptions.is_ext }
  }
  return sections
}

function ensureElementState(rough: LegacyRough, name: string): Record<string, unknown> {
  rough.el_rough = rough.el_rough || {}
  const current = rough.el_rough[name]
  if (current && typeof current === 'object') return current
  const next: Record<string, unknown> = {}
  rough.el_rough[name] = next
  return next
}

function writeSectionState(rough: LegacyRough, name: EditorSectionName, section: EditorSectionModel): void {
  if (name === 'settings') {
    const target = ensureElementState(rough, 'more_settings')
    if (section.open !== undefined) target.is_open = section.open
    if (section.activeTab !== undefined) target.tab_id = settingsTabs.indexOf(section.activeTab) + 1
    return
  }

  if (name === 'formatting') {
    if (section.open !== undefined) ensureElementState(rough, 'msg_txt').is_rich_edit = section.open
    return
  }

  if (section.open !== undefined) ensureElementState(rough, 'name_key').is_ext = section.open
}

export function readEditorFormModel(
  rough: LegacyRough,
  reason: string,
  source: EditorFormModel['source'] = 'legacy-rough',
): EditorFormModel {
  const body = legacySection(rough, 'body')
  const bodyText = legacySection(rough, 'body_textarea')
  const toAddress = legacySection(rough, 'to_address')
  const moreSettings = legacySection(rough, 'more_settings')
  const protect = legacySection(rough, 'protect')
  const ruling = legacySection(rough, 'ruling')
  const textData = bodyText[1]
  const nameScope = editorNameScope(rough)
  const settings: EditorFormModel['settings'] = {}
  EDITOR_SETTING_NAMES.forEach((name, index) => {
    const value = moreSettings[index]
    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') settings[name] = value
  })
  const showPin = Array.isArray(protect[0]) ? protect[0] : []
  const editPin = Array.isArray(protect[1]) ? protect[1] : []
  const parentMoreSettings = rough.static_rough?.msg_data?.msg_inputs?.more_settings
  const parentProtect = rough.static_rough?.msg_data?.msg_inputs?.protect
  const parentShowPin = Array.isArray(parentProtect?.[0]) ? parentProtect[0] : []
  const parentEditPin = Array.isArray(parentProtect?.[1]) ? parentProtect[1] : []
  const parentForcesShowPin = !rough.is_update
    && (
      (Array.isArray(parentMoreSettings) && legacyTrue(parentMoreSettings[15]))
      || legacyTrue(parentShowPin[2])
    )
  const parentForcesEditPin = !rough.is_update
    && (
      (Array.isArray(parentMoreSettings) && legacyTrue(parentMoreSettings[20]))
      || legacyTrue(parentEditPin[2])
    )

  return {
    nameScope: nameScope.scope,
    nameScopeParent: nameScope.parent,
    mode: rough.msg_type,
    isMsg: rough.is_msg,
    isUpdate: rough.is_update,
    draftElId: rough.draft_el_id,
    isDraftingMode: rough.is_drafting_mode === true,
    fields: {
      codeKey: stringValue(body[0]),
      subject: stringValue(body[1]),
      nameKey: localEditorName(stringValue(bodyText[0]), nameScope.scope),
      smartNameKey: localEditorName(stringValue(bodyText[3]), nameScope.scope),
      bodyText: stringValue(textData && typeof textData === 'object' ? (textData as Record<string, unknown>).msg_text : bodyText[2]),
      bodyIsHtml: textData && typeof textData === 'object'
        ? Boolean((textData as Record<string, unknown>).is_html)
        : undefined,
      recipientInput: stringValue(toAddress[0]),
    },
    settings,
    pins: {
      show: {
        enabled: typeof showPin[0] === 'boolean' ? showPin[0] : undefined,
        value: stringValue(showPin[1]),
        inherited: legacyTrue(showPin[2]) || parentForcesShowPin,
      },
      edit: {
        enabled: typeof editPin[0] === 'boolean' ? editPin[0] : undefined,
        value: stringValue(editPin[1]),
        inherited: legacyTrue(editPin[2]) || parentForcesEditPin,
      },
    },
    productCreation: readProductCreation(rough),
    ruling: {
      timeStart: numberValue(ruling[0]),
      timeStop: numberValue(ruling[1]),
      startEnabled: typeof ruling[3] === 'boolean' ? ruling[3] : undefined,
      stopEnabled: typeof ruling[4] === 'boolean' ? ruling[4] : undefined,
      hideTimeLocked: typeof ruling[5] === 'boolean' ? ruling[5] : undefined,
    },
    sections: readSectionStates(rough),
    source,
    reason,
    syncedAt: Date.now(),
  }
}

export function writeEditorFormModel(rough: LegacyRough, model: EditorFormModel | undefined): void {
  if (!model || (model.mode && rough.msg_type && model.mode !== rough.msg_type)) return
  const body = ensureSection(rough, 'body')
  if (model.fields.codeKey !== undefined) body[0] = model.fields.codeKey
  if (model.fields.subject !== undefined) body[1] = model.fields.subject

  const bodyText = ensureSection(rough, 'body_textarea')
  if (model.fields.nameKey !== undefined) bodyText[0] = model.fields.nameKey
  if (model.fields.smartNameKey !== undefined) bodyText[3] = model.fields.smartNameKey
  if (model.fields.bodyText !== undefined) setBodyTextValue(rough, model.fields.bodyText)
  if (model.fields.bodyIsHtml !== undefined) setBodyHtmlFlag(rough, model.fields.bodyIsHtml)

  const toAddress = ensureSection(rough, 'to_address')
  if (model.fields.recipientInput !== undefined) toAddress[0] = model.fields.recipientInput

  const settings = ensureSection(rough, 'more_settings')
  EDITOR_SETTING_NAMES.forEach((name, index) => {
    const value: EditorSettingValue | undefined = model.settings[name]
    if (value !== undefined) settings[index] = value
  })

  const protect = ensureSection(rough, 'protect')
  const showPin = Array.isArray(protect[0]) ? [...protect[0] as unknown[]] : []
  const editPin = Array.isArray(protect[1]) ? [...protect[1] as unknown[]] : []
  if (model.pins.show.enabled !== undefined) showPin[0] = model.pins.show.enabled
  if (model.pins.show.value !== undefined) showPin[1] = model.pins.show.value
  if (model.pins.edit.enabled !== undefined) editPin[0] = model.pins.edit.enabled
  if (model.pins.edit.value !== undefined) editPin[1] = model.pins.edit.value
  if (showPin.length) protect[0] = showPin
  if (editPin.length) protect[1] = editPin

  writeProductCreation(rough, model.productCreation || {})

  const ruling = ensureSection(rough, 'ruling')
  if (model.ruling.timeStart !== undefined) ruling[0] = model.ruling.timeStart
  if (model.ruling.timeStop !== undefined) ruling[1] = model.ruling.timeStop
  if (model.ruling.startEnabled !== undefined) ruling[3] = model.ruling.startEnabled
  if (model.ruling.stopEnabled !== undefined) ruling[4] = model.ruling.stopEnabled
  if (model.ruling.hideTimeLocked !== undefined) ruling[5] = model.ruling.hideTimeLocked

  for (const [name, section] of Object.entries(model.sections)) {
    if (section) writeSectionState(rough, name as EditorSectionName, section)
  }
}

function rootElement(): Element | null {
  return getMsgDeskRuntime().querySelector('[data-md-contract="editor.root"]')
}

function hasRequiredEditorContracts(): boolean {
  const runtime = getMsgDeskRuntime()
  return Boolean(
    runtime.querySelector('[data-md-contract="editor.root"]') &&
    runtime.querySelector('[data-md-contract="editor.form"]') &&
    runtime.querySelector('[data-md-contract="editor.body.textarea"]'),
  )
}

function firstField(root: Element, name: string): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
  return root.querySelector(`[name="${CSS.escape(name)}"]`)
}

function fieldValue(root: Element, name: string): string | undefined {
  const field = firstField(root, name)
  if (!field) return undefined
  return field.value
}

function checkboxValue(root: Element, name: string): boolean | undefined {
  const field = firstField(root, name)
  if (!(field instanceof HTMLInputElement)) return undefined
  return field.checked
}

function numericValue(root: Element, name: string): number | undefined {
  const raw = fieldValue(root, name)
  if (raw == null || raw === '') return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

function elementValue(element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): unknown {
  if (element instanceof HTMLInputElement && element.type === 'checkbox') return element.checked
  if (element instanceof HTMLSelectElement) {
    const parsed = Number.parseInt(element.value, 10)
    return Number.isNaN(parsed) ? element.value : parsed
  }
  return element.value
}

function setBodyTextValue(rough: LegacyRough, value: string): void {
  const bodyText = ensureSection(rough, 'body_textarea')
  const textObj = bodyText[1] && typeof bodyText[1] === 'object' ? bodyText[1] as Record<string, unknown> : {}
  bodyText[1] = {
    ...textObj,
    msg_text: value,
    is_dirty: textObj.msg_text !== value || textObj.is_dirty,
  }
  bodyText[2] = value
}

// Mirrors m34 _toggle_rich_edit's flag writes exactly:
// this_static.is_html = this_values.is_html = is_html (rough text object +
// the shared static msg_txt element state). The saver reads
// inputs_values.body_textarea[1].is_html to emit is_plain (m29 _saver_func).
function setBodyHtmlFlag(rough: LegacyRough, isHtml: boolean): void {
  const bodyText = ensureSection(rough, 'body_textarea')
  const textObj = bodyText[1] && typeof bodyText[1] === 'object' ? bodyText[1] as Record<string, unknown> : {}
  bodyText[1] = { ...textObj, is_html: isHtml }
  const staticRough = rough.static_rough as Record<string, any> | undefined
  if (staticRough) {
    staticRough.el_rough = staticRough.el_rough || {}
    const msgTxt = staticRough.el_rough.msg_txt
    if (msgTxt && typeof msgTxt === 'object') msgTxt.is_html = isHtml
    else staticRough.el_rough.msg_txt = { is_html: isHtml }
  }
}

function syncRecipientsFromLegacy(rough: LegacyRough, toAddress: unknown[]): void {
  try {
    const simple = rough?.el_rough?.to_address?.to_arr?.arr_to_simple?.()
    if (Array.isArray(simple)) toAddress[1] = cloneData(simple)
  } catch {
    // Recipient tokens are still legacy-owned during the transition.
  }
}

function recipientLabel(data: unknown[]): string {
  const name = String(data[0] ?? '').trim()
  if (!name) return ''
  return data[2] ? name : `@${name}`
}

function recipientKind(rough: LegacyRough, data: unknown[]): EditorRecipientSnapshot['kind'] {
  if (data[2]) return 'email'
  const mainCodeKey = rough?.static_rough?.msg_data?.code_key
  return mainCodeKey && data[1] === mainCodeKey ? 'self' : 'msg'
}

function syncRecipientsModelFromLegacy(rough: LegacyRough, reason: string): boolean {
  const toAddress = ensureSection(rough, 'to_address')
  syncRecipientsFromLegacy(rough, toAddress)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  getMsgDeskRuntime().dispatchCustomEvent('msg:v2-recipient-sync')
  return true
}

function writeDomFieldsToRough(rough: LegacyRough): void {
  const root = rootElement()
  if (!root) return

  const body = ensureSection(rough, 'body')
  const codeKey = fieldValue(root, 'code_key')
  const subject = fieldValue(root, 'subject')
  if (codeKey !== undefined) body[0] = codeKey
  if (subject !== undefined) body[1] = subject

  const bodyText = ensureSection(rough, 'body_textarea')
  const extNameKey = fieldValue(root, 'name_key')
  const msgText = fieldValue(root, 'msg_text')
  const smartNameKey = fieldValue(root, 'name_key_smart')
  if (extNameKey !== undefined) bodyText[0] = extNameKey
  if (msgText !== undefined) {
    setBodyTextValue(rough, msgText)
  }
  if (smartNameKey !== undefined) bodyText[3] = smartNameKey

  const toAddress = ensureSection(rough, 'to_address')
  const rawToAddress = fieldValue(root, 'to_address')
  if (rawToAddress !== undefined) toAddress[0] = rawToAddress
  syncRecipientsFromLegacy(rough, toAddress)

  const moreSettings = ensureSection(rough, 'more_settings')
  EDITOR_SETTING_NAMES.forEach((name, index) => {
    if (checkboxSettingIndexes.has(index)) {
      const checked = checkboxValue(root, name)
      if (checked !== undefined) moreSettings[index] = checked
      return
    }
    if (numericSettingIndexes.has(index)) {
      // The DOM displays option 1 for an unchecked pair (m31 display rule);
      // the rough must keep 0 exactly as legacy did, or save requests drift.
      const master = comboMasterByName.get(name)
      if (master && checkboxValue(root, master) === false) {
        moreSettings[index] = 0
        return
      }
      const value = numericValue(root, name)
      if (value !== undefined) moreSettings[index] = value
      return
    }
    const value = fieldValue(root, name)
    if (value !== undefined) moreSettings[index] = value
  })

  const protect = ensureSection(rough, 'protect')
  const showPin = Array.isArray(protect[0]) ? [...(protect[0] as unknown[])] : []
  const editPin = Array.isArray(protect[1]) ? [...(protect[1] as unknown[])] : []
  const isShowPin = checkboxValue(root, 'is_show_pin')
  const showPinValue = fieldValue(root, 'show_pin')
  const isEditPin = checkboxValue(root, 'is_edit_pin')
  const editPinValue = fieldValue(root, 'edit_pin')
  if (isShowPin !== undefined) showPin[0] = isShowPin
  if (showPinValue !== undefined) showPin[1] = showPinValue
  if (isEditPin !== undefined) editPin[0] = isEditPin
  if (editPinValue !== undefined) editPin[1] = editPinValue
  if (showPin.length) protect[0] = showPin
  if (editPin.length) protect[1] = editPin

  const ruling = ensureSection(rough, 'ruling')
  const timeStart = numericValue(root, 'time_start')
  const timeStop = numericValue(root, 'time_stop')
  const isRuleStart = checkboxValue(root, 'is_rule_start')
  const isRuleStop = checkboxValue(root, 'is_rule_stop')
  const isHideTimeLocked = checkboxValue(root, 'is_hide_time_locked')
  if (timeStart !== undefined) ruling[0] = timeStart
  if (timeStop !== undefined) ruling[1] = timeStop
  if (isRuleStart !== undefined) ruling[3] = isRuleStart
  if (isRuleStop !== undefined) ruling[4] = isRuleStop
  if (isHideTimeLocked !== undefined) ruling[5] = isHideTimeLocked
}

export function syncEditorRoughModel(reason = 'legacy-rough'): EditorFormModel | undefined {
  if (!hasRequiredEditorContracts()) return undefined
  const rough = readLegacyRough()
  if (!rough) return undefined
  const model = readEditorFormModel(rough, reason)
  treeStore.mutate.setEditorFormModel(model)
  return model
}

export function flushEditorModelToLegacyRough(reason = 'react-facade:flush'): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  writeEditorFormModel(rough, treeStore.getState().templateData.editorFormModel)
  writeDomFieldsToRough(rough)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorRoughInputValue(
  section: LegacyRoughSection,
  index: number,
  value: unknown,
  reason = 'react-facade:set-input',
): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  ensureSection(rough, section)[index] = value
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function syncEditorInputElement(
  element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  reason = 'react-facade:input',
): boolean {
  const rough = readLegacyRough()
  const name = element.name
  if (!rough || !name) return false

  if (name === 'subject') ensureSection(rough, 'body')[1] = String(element.value ?? '')
  else if (name === 'code_key') ensureSection(rough, 'body')[0] = String(element.value ?? '')
  else if (name === 'name_key') ensureSection(rough, 'body_textarea')[0] = String(element.value ?? '')
  else if (name === 'name_key_smart') ensureSection(rough, 'body_textarea')[3] = String(element.value ?? '')
  else if (name === 'msg_text') setBodyTextValue(rough, String(element.value ?? ''))
  else if (name === 'to_address') ensureSection(rough, 'to_address')[0] = String(element.value ?? '')
  else if (moreSettingIndexByName.has(name)) {
    const settings = ensureSection(rough, 'more_settings')
    settings[moreSettingIndexByName.get(name)!] = elementValue(element)
    const controlledCombos = controlledComboIndexesByMaster.get(name)
    if (controlledCombos && element instanceof HTMLInputElement && element.type === 'checkbox') {
      for (const comboIndex of controlledCombos) {
        const current = Number(settings[comboIndex])
        settings[comboIndex] = element.checked
          ? (Number.isFinite(current) && current >= 1 ? current : 1)
          : 0
      }
    }
  }
  else if (name === 'is_show_pin' || name === 'show_pin' || name === 'is_edit_pin' || name === 'edit_pin') {
    const protect = ensureSection(rough, 'protect')
    const index = name === 'is_show_pin' || name === 'show_pin' ? 0 : 1
    const pin = Array.isArray(protect[index]) ? [...(protect[index] as unknown[])] : []
    if (name === 'is_show_pin' || name === 'is_edit_pin') pin[0] = element instanceof HTMLInputElement ? element.checked : Boolean(element.value)
    else pin[1] = String(element.value ?? '')
    protect[index] = pin
  } else if (name === 'time_start') ensureSection(rough, 'ruling')[0] = Number.parseInt(element.value || '0', 10) || 0
  else if (name === 'time_stop') ensureSection(rough, 'ruling')[1] = Number.parseInt(element.value || '0', 10) || 0
  else if (name === 'is_rule_start') ensureSection(rough, 'ruling')[3] = element instanceof HTMLInputElement ? element.checked : Boolean(element.value)
  else if (name === 'is_rule_stop') ensureSection(rough, 'ruling')[4] = element instanceof HTMLInputElement ? element.checked : Boolean(element.value)
  else if (name === 'is_hide_time_locked') ensureSection(rough, 'ruling')[5] = element instanceof HTMLInputElement ? element.checked : Boolean(element.value)
  else return false

  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorBodyText(value: string): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  setBodyTextValue(rough, value)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, 'react-body:text', 'react-facade'))
  return true
}

export function setEditorNameScope(enabled: boolean): boolean {
  const rough = readLegacyRough()
  if (!rough || rough.is_update || Number(rough.draft_el_id) > 0) return false
  const { parent } = editorNameScope(rough)
  rough.name_scope = enabled ? parent : ''
  // A verdict belongs to the full address, so changing scope invalidates it.
  for (const name of ['name_key', 'name_key_smart']) {
    const state = ensureElementState(rough, name)
    delete state.ok_value
    state.state_id = 0
  }
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, 'react-name:scope', 'react-facade'))
  return true
}

export function setEditorProductCreation(
  values: Partial<EditorProductCreationModel>,
  reason = 'react-product-creation:update',
): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  writeProductCreation(rough, values)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorBodyHtml(isHtml: boolean, value?: string, reason = 'react-body:html-mode'): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  setBodyHtmlFlag(rough, isHtml)
  if (value !== undefined) setBodyTextValue(rough, value)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorMoreSetting(name: (typeof EDITOR_SETTING_NAMES)[number], value: unknown, reason = 'react-settings:more'): boolean {
  const rough = readLegacyRough()
  const index = moreSettingIndexByName.get(name)
  if (!rough || index === undefined) return false
  ensureSection(rough, 'more_settings')[index] = value
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorRulingValue(field: EditorRulingField, value: unknown, reason = 'react-settings:ruling'): boolean {
  const rough = readLegacyRough()
  const index = rulingIndexByField[field]
  if (!rough || index === undefined) return false
  ensureSection(rough, 'ruling')[index] = value
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorSectionState(
  sectionName: EditorSectionName,
  state: { open?: boolean; activeTab?: EditorSettingsTab },
  reason = 'react-section',
): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  writeSectionState(rough, sectionName, state)
  treeStore.mutate.setEditorFormModel(readEditorFormModel(rough, reason, 'react-facade'))
  return true
}

export function setEditorSubject(value: string): boolean {
  return setEditorRoughInputValue('body', 1, value, 'react-head:subject')
}

export function setEditorCodeKey(value: string): boolean {
  return setEditorRoughInputValue('body', 0, value, 'react-head:code-key')
}

export function setEditorExtendedNameKey(value: string): boolean {
  return setEditorRoughInputValue('body_textarea', 0, value, 'react-head:name-key-ext')
}

export function setEditorSmartNameKey(value: string): boolean {
  return setEditorRoughInputValue('body_textarea', 3, value, 'react-head:name-key-smart')
}

function parseLegacyJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

export function generateEditorSmartNameKey(rule: string): Promise<EditorNameKeyResult> {
  const runtime = getMsgDeskRuntime()
  const utils = runtime.legacyUtils
  if (!utils?.ajax_get) return Promise.resolve({ value: '', status: 'wrong' })
  setEditorSmartNameKey('')

  return new Promise(resolve => {
    try {
      utils.ajax_get('autonamekey', {
        action: 'autonamekey',
        rule,
        text: '',
        time_str: new Date().toString(),
      }, (resultData: string) => {
        const parsed = parseLegacyJson(resultData)
        const value = parsed == null ? '' : String(parsed)
        setEditorSmartNameKey(value.trim())
        resolve({ value, status: value.trim() ? 'ok' : 'idle' })
      })
    } catch {
      resolve({ value: '', status: 'wrong' })
    }
  })
}

export function checkEditorSmartNameKey(rawValue: string, extended = false): Promise<EditorNameKeyResult> {
  const value = rawValue.trim()
  const setName = extended ? setEditorExtendedNameKey : setEditorSmartNameKey
  setName(value)
  if (value.includes('@')) return Promise.resolve({ value, status: 'wrong' })
  if (!value) return generateEditorSmartNameKey('0').then(result => {
    if (extended) setEditorExtendedNameKey(result.value)
    return result
  })

  const runtime = getMsgDeskRuntime()
  const utils = runtime.legacyUtils
  if (!utils?.ajax_get) return Promise.resolve({ value, status: 'wrong' })
  const rough = readLegacyRough()
  const forceCodeKey = extended && (rough?.el_rough?.code_key?.is_open == null || !rough.inputs_values?.body?.[0])

  return new Promise(resolve => {
    try {
      utils.ajax_get('check_keys', {
        action: 'check_keys',
        is_force_ok: '1',
        is_force_both: forceCodeKey ? '1' : '0',
        name_key: value,
        code_key: '',
        to_address: '',
        sub_key: '',
      }, (resultData: string) => {
        const parsed = parseLegacyJson(resultData)
        const data = parsed && typeof parsed === 'object' ? parsed as Record<number, unknown> : null
        const corrected = data?.[3] == null ? '' : String(data[3])
        const isWrong = data ? data[0] === 'err_keys' && data[2] === false : !corrected
        const resultValue = corrected || value
        if (rough) {
          const state = ensureElementState(rough, extended ? 'name_key' : 'name_key_smart')
          state.state_id = isWrong || !corrected ? 1 : 3
          if (!isWrong && corrected) state.ok_value = resultValue
          else delete state.ok_value
          if (forceCodeKey && data?.[1]) {
            ensureElementState(rough, 'code_key').ok_value = String(data[1])
            setEditorCodeKey(String(data[1]))
          }
        }
        setName(resultValue.trim())
        resolve({ value: resultValue, status: isWrong || !corrected ? 'wrong' : 'ok' })
      })
    } catch {
      resolve({ value, status: 'wrong' })
    }
  })
}

export function syncEditorRecipientsFromLegacy(reason = 'react-recipients:sync'): boolean {
  const rough = readLegacyRough()
  if (!rough) return false
  return syncRecipientsModelFromLegacy(rough, reason)
}

function finishEditorRecipientCommit(value: string, kind: 'email' | 'msg'): EditorRecipientCommitResult {
  const runtime = getMsgDeskRuntime()
  syncEditorRecipientsFromLegacy(`react-recipients:insert-${kind}`)
  runtime.dispatchCustomEvent('msg:v2-recipient-insert', { label: value, kind })
  setEditorRoughInputValue('to_address', 0, '', 'react-recipients:clear-input')
  return { committed: true, kind, label: value }
}

// Legacy to_address email rule — the exact validator regex from the legacy
// checker (m25_new_checker `to_address` addMethod). Kept 1:1: it is the
// frozen behavioral definition of "this text is an email recipient".
const LEGACY_TO_ADDRESS_EMAIL_RE = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i

export function commitEditorRecipient(rawValue: string): Promise<EditorRecipientCommitResult> {
  const value = rawValue.trim()
  if (!value) return Promise.resolve({ committed: false })

  const runtime = getMsgDeskRuntime()
  const toAddress = runtime.facade.editorToAddress()
  if (!toAddress?.insert) return Promise.resolve({ committed: false })

  // Legacy validation (checker to_address rule): a value is an EMAIL when it
  // matches the frozen regex, a MSG when it starts with '@'; both require
  // length >= 4. Anything else is is_valid 0 and NEVER becomes a token
  // (legacy _check_to_address early-returns) — previously the React path
  // inserted any non-@ text as an "email" token.
  const isEmail = LEGACY_TO_ADDRESS_EMAIL_RE.test(value)
  const isMsg = !isEmail && value.startsWith('@')
  if (value.length < 4 || (!isEmail && !isMsg)) {
    return Promise.resolve({ committed: false, reason: 'invalid' })
  }

  if (isEmail) {
    // Legacy _to_msg_insert returns TRUE for a refused duplicate (one email
    // max, unique code keys) and undefined on a successful insert; the
    // legacy checker keeps the typed value on a duplicate and clears it on
    // success. Mirror that — only a non-true result is a commit.
    const inserted = toAddress.insert([[value, value, true, false]], true)
    return Promise.resolve(inserted === true
      ? { committed: false, reason: 'duplicate' }
      : finishEditorRecipientCommit(value, 'email'))
  }

  const name = value.slice(1)
  if (!name || !runtime.legacyUtils?.ajax_get) return Promise.resolve({ committed: false })

  return new Promise(resolve => {
    try {
      runtime.legacyUtils.ajax_get('check_keys', {
        action: 'check_keys',
        is_force_ok: '1',
        is_force_both: '0',
        name_key: '',
        code_key: '',
        to_address: name,
        sub_key: '',
      }, (resultData: string) => {
        if (!resultData || resultData === '0') {
          resolve({ committed: false, reason: 'not-found' })
          return
        }
        const inserted = toAddress.insert([[value, resultData, false, false]], true)
        resolve(inserted === true
          ? { committed: false, reason: 'duplicate' }
          : finishEditorRecipientCommit(value, 'msg'))
      })
    } catch {
      resolve({ committed: false })
    }
  })
}

export function collectEditorRecipientsFromLegacy(): EditorRecipientSnapshot[] {
  const rough = readLegacyRough()
  const items = rough?.el_rough?.to_address?.to_arr?.[1]
  if (!rough || !Array.isArray(items)) return []

  const recipients: EditorRecipientSnapshot[] = []
  for (const item of items) {
    if (!Array.isArray(item) || !Array.isArray(item[0])) continue
    const id = Number(item[2])
    const data = item[0] as unknown[]
    if (data.length < 3) continue
    const label = recipientLabel(data)
    if (!Number.isFinite(id) || !label) continue
    recipients.push({
      id,
      label,
      kind: recipientKind(rough, data),
      // Legacy non_editable flag (data[3]): the CSS hides the remove control
      // on such tokens (self token on new_msg/new_answer/update_msg).
      editable: !data[3],
      data: cloneData(data),
    })
  }
  return recipients
}

export function removeEditorRecipientFromLegacy(id: number): boolean {
  const rough = readLegacyRough()
  const toArr = rough?.el_rough?.to_address?.to_arr
  if (!rough || !toArr?.arr_remove || !Number.isFinite(id)) return false
  try {
    toArr.arr_remove(id)
  } catch {
    return false
  }
  return syncRecipientsModelFromLegacy(rough, 'react-recipients:remove')
}

export function getEditorRoughSnapshot(reason = 'debug'): EditorFormModel | undefined {
  const rough = readLegacyRough()
  return rough ? readEditorFormModel(rough, reason) : undefined
}

const runtime = getMsgDeskRuntime()
const existingFacade = runtime.getGlobal<Record<string, unknown>>('msgdskV2EditorRoughFacade')
if (!existingFacade) runtime.setGlobal('msgdskV2EditorRoughFacade', {
  sync: syncEditorRoughModel,
  flush: flushEditorModelToLegacyRough,
  setInput: setEditorRoughInputValue,
  syncInputElement: syncEditorInputElement,
  setBodyText: setEditorBodyText,
  setMoreSetting: setEditorMoreSetting,
  setProductCreation: setEditorProductCreation,
  setRulingValue: setEditorRulingValue,
  setSubject: setEditorSubject,
  setCodeKey: setEditorCodeKey,
  setExtendedNameKey: setEditorExtendedNameKey,
  setSmartNameKey: setEditorSmartNameKey,
  syncRecipients: syncEditorRecipientsFromLegacy,
  commitRecipient: commitEditorRecipient,
  collectRecipients: collectEditorRecipientsFromLegacy,
  removeRecipient: removeEditorRecipientFromLegacy,
  snapshot: getEditorRoughSnapshot,
})
